import { Bell, CheckCheck } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { bk, useBusiness } from '@/lib/business';
import { get, post } from '@/lib/api';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  read: boolean;
  createdAt: string;
}
interface NotificationsResponse {
  unreadCount: number;
  data: Notification[];
}

export function NotificationsMenu() {
  const { businessId } = useBusiness();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = bk(businessId, 'notifications');
  const q = useQuery({ queryKey: key, queryFn: () => get<NotificationsResponse>('/notifications', { pageSize: 15 }), refetchInterval: 60_000, enabled: !!businessId });
  const readAll = useMutation({ mutationFn: () => post('/notifications/read-all'), onSuccess: () => qc.invalidateQueries({ queryKey: key }) });
  const readOne = useMutation({ mutationFn: (id: string) => post(`/notifications/${id}/read`), onSuccess: () => qc.invalidateQueries({ queryKey: key }) });
  const unread = q.data?.unreadCount ?? 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}>
          <Bell className="size-4" />
          {unread > 0 && <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-danger ring-2 ring-background" aria-hidden />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <h2 className="text-sm font-semibold">Notifications</h2>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={() => readAll.mutate()}>
              <CheckCheck className="size-4" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto" aria-live="polite">
          {q.isLoading && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
          {q.isError && <p className="p-4 text-sm text-danger">Couldn’t load notifications.</p>}
          {q.data && q.data.data.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">You’re all caught up.</p>}
          <ul>
            {q.data?.data.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={cn('flex w-full gap-2 border-b px-3 py-2.5 text-left last:border-0 hover:bg-accent/60', !n.read && 'bg-accent/40')}
                  onClick={() => {
                    if (!n.read) readOne.mutate(n.id);
                    if (n.link) navigate(n.link);
                  }}
                >
                  <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.read ? 'bg-transparent' : 'bg-primary')} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{n.title}</span>
                    {n.body && <span className="line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>}
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">{relativeTime(n.createdAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}
